import { User } from '@qogita/canary-types';
import { useQuery } from '@tanstack/react-query';

import { useRefreshSession } from '#hooks/api/usePostAuthRefresh';
import { ERRORS } from '#src/constants/copy';
import { BASE_URL } from '#src/constants/featureFlags';
import { ONE_MINUTE_MS } from '#src/constants/time';
import { FailedToFetchError } from '#src/utils/errors';
import { getAccessToken, getIsAccessTokenValid } from '#utils/authentication';
import { getResponse } from '#utils/getResponse';
import { getUserQueryKey } from '#utils/queryKey';

const getUser = async () => {
  const response = await fetch(`${BASE_URL}/user/`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getAccessToken()}`,
    },
  }).catch(() => {
    throw new FailedToFetchError(ERRORS.failedToFetch);
  });

  return getResponse<User>({
    name: 'getUser',
    response,
    defaultErrorMessage: ERRORS.getUser.default,
  });
};

export const useGetUser = () => {
  const { data } = useRefreshSession();

  return useQuery<User, Error>({
    queryKey: getUserQueryKey(),
    queryFn: () => getUser(),
    /*
      Disabling the query when the access token is not present or invalid.
      This protects unauthenticated sessions to keep on trying to fetch the user.
     */
    enabled: getIsAccessTokenValid(data?.refreshToken?.accessToken || ''),
    refetchInterval: 25 * ONE_MINUTE_MS,
  });
};
